/**
 * Brief 3.5 PR 4 — Plain-language translations for the Admin UI.
 *
 * Engineer codes / underscore strings are NOT acceptable in the
 * compliance back-office. Everything that touches an operator's eyes
 * must be plain English.
 *
 * This is the FRONTEND-side dict. PR 6 will land
 * `backend/services/plain_language.py` as the canonical source; this
 * file's structure mirrors it so PR 6 can simply replace these
 * constants with API-sourced values without restructuring the
 * consumers. PR 5 will then layer Telegram-specific terms on top of
 * the same dict.
 *
 * Rule of thumb when adding entries:
 *   • No underscores, no camelCase, no raw enums.
 *   • Sentence-case (not Title Case) — UI shells handle case.
 *   • Helper copy is short, actionable, never blames the merchant.
 */

// ---- Document slot keys (registry from nerea_doc_schemas.SCHEMA_FOR_DOC_KEY) ----

export const DOC_KEY_LABELS = {
  cert_incorporation:      "Certificate of incorporation",
  memorandum_articles:     "Memorandum & articles",
  good_standing:           "Certificate of good standing",
  audited_accounts:        "Audited or management accounts",
  ownership_chart:         "Group structure / ownership chart",
  director_ubo_id:         "Government ID (director or UBO)",
  director_ubo_address:    "Proof of address (director or UBO)",
  ubo_declaration:         "Signed UBO declaration",
  bank_statement:          "Bank statement",
  bank_reference:          "Bank reference letter",
  processing_statements:   "Payment processing statements",
  customer_terms:          "Customer-facing terms, privacy & refunds",
  aml_policy:              "AML / CTF policy",
  regulatory_licences:     "Regulatory licences",
  data_protection:         "GDPR / data protection",
  vertical_authorisations: "Vertical-specific authorisations",
  other_documents:         "Other documents",
};

// ---- Classifier verdicts (`doc_type_classified` strings) ----

export const DOC_TYPE_LABELS = {
  passport:                          "Passport",
  national_id:                       "National ID",
  driving_licence:                   "Driving licence",
  residence_permit:                  "Residence permit",
  government_id:                     "Government ID",
  utility_bill:                      "Utility bill",
  bank_statement:                    "Bank statement",
  government_letter:                 "Government letter",
  lease:                             "Lease agreement",
  proof_of_address:                  "Proof of address",
  certificate_of_incorporation:      "Certificate of incorporation",
  memorandum_articles:               "Memorandum & articles",
  articles_of_association:           "Articles of association",
  certificate_of_good_standing:      "Certificate of good standing",
  audited_accounts:                  "Audited accounts",
  management_accounts:               "Management accounts",
  financial_statements:              "Financial statements",
  group_structure_chart:             "Group structure chart",
  ownership_chart:                   "Ownership chart",
  signed_ubo_declaration:            "Signed UBO declaration",
  ubo_declaration:                   "UBO declaration",
  bank_reference_letter:             "Bank reference letter",
  kyc_confirmation:                  "KYC confirmation",
  processing_statement:              "Processing statement",
  merchant_statement:                "Merchant statement",
  terms_of_service:                  "Terms of service",
  privacy_policy:                    "Privacy policy",
  refund_policy:                     "Refund policy",
  customer_terms:                    "Customer terms",
  aml_policy:                        "AML policy",
  aml_ctf_policy:                    "AML/CTF policy",
  regulatory_licence:                "Regulatory licence",
  licence:                           "Licence",
  gdpr_policy:                       "GDPR policy",
  data_protection_policy:            "Data protection policy",
  authorisation:                     "Authorisation",
  vertical_specific_authorisation:   "Vertical-specific authorisation",
  other:                             "Other document",
  unknown:                           "Couldn't identify",
};

// ---- Extracted field names (subset shared across schemas) ----

export const FIELD_LABELS = {
  legal_name:                  "Legal name",
  company_legal_name:          "Company legal name",
  company_registered_address:  "Registered address",
  registration_number:         "Registration number",
  registered_office_address:   "Registered office",
  country_of_incorporation:    "Country of incorporation",
  date_of_incorporation:       "Date of incorporation",
  jurisdiction:                "Jurisdiction",
  share_capital_total:         "Share capital",
  share_capital_currency:      "Share capital currency",
  directors_list:              "Directors",
  shareholders_list:           "Shareholders",
  ubo_natural_persons:         "Ultimate beneficial owners",
  declared_ubos:               "Declared UBOs",
  ultimate_percentage:         "Ultimate ownership %",
  percentage:                  "Ownership %",
  percentage_held_by_parent:   "% held by parent",
  parent_entity_name:          "Parent entity",
  parent_entity_jurisdiction:  "Parent jurisdiction",
  path_through_entities:       "Ownership path",
  full_name:                   "Full name",
  name:                        "Name",
  holder_name:                 "Holder name",
  account_holder_name:         "Account holder",
  account_holder_address:      "Account holder address",
  date_of_birth:               "Date of birth",
  place_of_birth:              "Place of birth",
  nationality:                 "Nationality",
  residential_address:         "Residential address",
  role:                        "Role",
  document_type:               "Document type",
  document_type_guess:         "Document type (best guess)",
  document_number:             "Document number",
  document_date:               "Document date",
  document_issuer:             "Document issuer",
  document_summary:            "Summary",
  issuer_name:                 "Issuer",
  issuing_authority:           "Issuing authority",
  issuing_body:                "Issuing body",
  issuing_country:             "Issuing country",
  issued_at:                   "Issued",
  expires_at:                  "Expires",
  bank_name:                   "Bank",
  iban:                        "IBAN",
  bic_swift:                   "BIC / SWIFT",
  currency:                    "Currency",
  opening_balance:             "Opening balance",
  closing_balance:             "Closing balance",
  statement_period_start:      "Period start",
  statement_period_end:        "Period end",
  period_start:                "Period start",
  period_end:                  "Period end",
  is_audited:                  "Audited",
  is_management_accounts:      "Management accounts",
  auditor_name:                "Auditor",
  total_revenue:               "Total revenue",
  total_revenue_currency:      "Revenue currency",
  net_profit_or_loss:          "Net profit / loss",
  total_volume:                "Total volume",
  total_volume_currency:       "Volume currency",
  total_transaction_count:     "Transaction count",
  chargeback_count_or_ratio:   "Chargebacks",
  refund_ratio:                "Refund ratio",
  refund_window_days:          "Refund window (days)",
  psp_or_acquirer_name:        "PSP / acquirer",
  merchant_name:               "Merchant name",
  is_account_in_good_standing: "Account in good standing",
  status_text:                 "Status",
  relationship_start_date:     "Relationship started",
  signed_at:                   "Signed",
  signatory_name:              "Signatory",
  signature_present:           "Signature on file",
  mlro_named:                  "MLRO named",
  mlro_name:                   "MLRO",
  dpo_named:                   "DPO named",
  dpo_name:                    "DPO",
  policy_version:              "Policy version",
  last_reviewed_date:          "Last reviewed",
  last_updated_date:           "Last updated",
  licence_holder_name:         "Licence holder",
  licence_number:              "Licence number",
  licence_type:                "Licence type",
  authorisation_type:          "Authorisation type",
  entities_list:               "Entities",
  country:                     "Country",
};

// ---- Merchant lifecycle status (mirrors merchant_applications.status) ----

export const STATUS_LABELS = {
  draft:          "Draft",
  submitted:      "Submitted for review",
  under_review:   "Under review",
  info_requested: "Info requested",
  approved:       "Approved",
  live:           "Live",
  rejected:       "Rejected",
  declined:       "Declined",
  cancelled:      "Cancelled",
  withdrawn:      "Withdrawn",
  terminated:     "Terminated",
};

// ---- Plain-language helpers ----

/** Translate a merchant lifecycle status key. */
export function plainStatus(key) {
  if (!key) return "—";
  return STATUS_LABELS[key] || _humanize(key);
}

/** Translate a doc_key (registry slot) into its operator-facing label. */
export function plainDocKey(key) {
  if (!key) return "—";
  return DOC_KEY_LABELS[key] || _humanize(key);
}

/** Translate a classifier verdict string into a plain-English label. */
export function plainDocType(key) {
  if (!key) return "—";
  return DOC_TYPE_LABELS[key] || _humanize(key);
}

/** Translate a schema field name. */
export function plainFieldName(key) {
  if (!key) return "—";
  return FIELD_LABELS[key] || _humanize(key);
}

/**
 * Plain-language extraction status.
 * Returns { label, tone, helper } where tone ∈ ok|warn|bad.
 */
export function plainExtractionStatus(status) {
  if (!status) return { label: "Not run", tone: "muted", helper: "" };
  if (status === "success") return {
    label: "Verified",
    tone: "ok",
    helper: "All required fields were read with high confidence.",
  };
  if (status === "manually_verified") return {
    label: "Verified by AM",
    tone: "ok",
    helper: "AI flagged this document but the account manager confirmed it manually.",
  };
  if (status === "partial") return {
    label: "Some fields couldn't be read",
    tone: "warn",
    helper: "Most of the document was read, but a few fields need a manual check.",
  };
  if (status === "failed") return {
    label: "Couldn't read this document",
    tone: "bad",
    helper: "The document was unreadable or didn't match the expected type.",
  };
  return { label: _humanize(status), tone: "muted", helper: "" };
}

/**
 * Plain-language confidence tier.
 * @param {number|null|undefined} score 0..1
 * @returns {{label: string, helper: string, tone: 'ok'|'warn'|'bad'}|null}
 */
export function confidenceTier(score) {
  if (typeof score !== "number" || isNaN(score)) return null;
  if (score >= 0.8) return {
    label: "High confidence",
    helper: "Nerea is sure about this value.",
    tone: "ok",
  };
  if (score >= 0.6) return {
    label: "Medium confidence — please verify",
    helper: "Nerea found this value, but it's worth a quick eyeball.",
    tone: "warn",
  };
  return {
    label: "Low confidence — likely wrong",
    helper: "Treat this value as a starting point only.",
    tone: "bad",
  };
}

/** Plain-language severity copy for a finding badge. */
export function plainFindingSeverity(severity) {
  if (!severity) return null;
  if (severity === "critical")     return { label: "Blocks approval",    tone: "bad" };
  if (severity === "suspicious")   return { label: "Worth a look",       tone: "warn" };
  if (severity === "minor")        return { label: "FYI",                tone: "muted" };
  if (severity === "informational")return { label: "Informational",      tone: "muted" };
  return { label: _humanize(severity), tone: "muted" };
}

// ---- Internal ----

function _humanize(s) {
  // Last-resort fallback for unknown keys. Replace _/- with spaces and
  // capitalize the first letter. Never throws.
  return String(s)
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/^./, (c) => c.toUpperCase());
}
